/**
 * The SignalR connection, and the coalescing layer in front of it.
 *
 * Two problems are solved here, and the second is the interesting one.
 *
 * **Connection.** One hub connection for the whole application, shared by every screen, with
 * automatic reconnect. Pages subscribe to the message types they care about and unsubscribe on
 * unmount; the connection itself outlives them.
 *
 * **Coalescing.** §41 asks that the UI not repaint for every incoming event. A busy organization
 * can emit several events per second per session, and React re-rendering a floor of forty cards on
 * each one is wasted work that shows up as jank. So updates are buffered and flushed on an
 * animation frame: many messages become one render, the newest state for each session wins, and
 * the UI still converges on the truth within ~16ms. Nothing is dropped that matters — only
 * intermediate states nobody could have perceived.
 */

import * as signalR from '@microsoft/signalr'
import type { BugHuntEvent, LiveSession, RoomChatMessage, TimelineEntry } from './types'
import { getToken } from './auth'

export type ConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'disconnected'

type Handler<T> = (payload: T) => void

const HUB_URL = import.meta.env?.VITE_HUB_URL ?? '/hubs/live'

class RealtimeClient {
  private connection: signalR.HubConnection | null = null
  private state: ConnectionState = 'disconnected'

  /**
   * Which attempt owns the state.
   *
   * `start()` is asynchronous, and React StrictMode mounts, unmounts and remounts in development,
   * so a second `start()` routinely begins while the first is still negotiating. The first one
   * then loses its race and reports `disconnected` — over the top of a connection that is live.
   * The symptom is a connected socket behind a "disconnected" badge, which is worse than either
   * honest answer. Every attempt takes a ticket on the way in and only touches shared state while
   * the ticket is still current.
   */
  private generation = 0

  private readonly sessionHandlers = new Set<Handler<LiveSession[]>>()
  private readonly timelineHandlers = new Set<Handler<{ sessionId: string; entry: TimelineEntry }>>()
  private readonly counterHandlers = new Set<Handler<void>>()
  private readonly bugHandlers = new Set<Handler<BugHuntEvent[]>>()
  private readonly roomHandlers = new Set<Handler<RoomChatMessage>>()
  private readonly queuedHandlers = new Set<Handler<string>>()
  private readonly inboxHandlers = new Set<Handler<void>>()
  private readonly stateHandlers = new Set<Handler<ConnectionState>>()

  /** Newest update per session id, flushed together on the next frame. */
  private pendingSessions = new Map<string, LiveSession>()
  private pendingBugEvents: BugHuntEvent[] = []
  private flushHandle: number | null = null

  /** Counter refreshes are throttled harder: they cause a refetch, not just a repaint. */
  private counterTimer: ReturnType<typeof setTimeout> | null = null

  async start(): Promise<void> {
    if (this.connection) return

    const generation = ++this.generation
    const isCurrent = (): boolean => this.generation === generation

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(HUB_URL, {
        // Read lazily on every (re)connect: a token refreshed since the page loaded is used, and a
        // signed-out user does not silently reconnect with a stale one.
        accessTokenFactory: () => getToken() ?? '',
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 20000])
      .configureLogging(signalR.LogLevel.Warning)
      .build()

    connection.on('WorkSessionChanged', (update: LiveSession) => {
      this.pendingSessions.set(update.workSessionId, update)
      this.scheduleFlush()
    })

    connection.on('TimelineAppended', (sessionId: string, entry: TimelineEntry) => {
      // Timeline entries are not coalesced: each one is a distinct row a user may be reading, and
      // collapsing them would lose content rather than intermediate states.
      this.timelineHandlers.forEach((handler) => handler({ sessionId, entry }))
    })

    connection.on('DashboardCountersChanged', () => {
      if (this.counterTimer) return
      this.counterTimer = setTimeout(() => {
        this.counterTimer = null
        this.counterHandlers.forEach((handler) => handler())
      }, 1500)
    })

    // Not batched: a line of chat is shown as it is said, and there are few of them.
    connection.on('RoomMessage', (message: RoomChatMessage) => {
      this.roomHandlers.forEach((handler) => handler(message))
    })

    // A command joined this person's queue (sent from the web, the phone, a schedule): their desktop
    // app asks for it now. Only their own connections hear it (LiveHub.DeveloperGroup).
    // Something in the person's inbox changed (docs/refactor/03-caixa-de-entrada.md): ask for it again.
    connection.on('InboxChanged', () => {
      this.inboxHandlers.forEach((handler) => handler(undefined))
    })
    connection.on('PromptQueued', (promptId: string) => {
      this.queuedHandlers.forEach((handler) => handler(promptId))
    })

    connection.on('BugHuntEvent', (event: BugHuntEvent) => {
      this.pendingBugEvents.push(event)
      this.scheduleFlush()
    })

    // These fire for the lifetime of this particular connection, which may outlive its relevance
    // if it was superseded, so they carry the same guard as the initial handshake.
    connection.onreconnecting(() => isCurrent() && this.setState('reconnecting'))
    connection.onreconnected(() => isCurrent() && this.setState('connected'))
    connection.onclose(() => isCurrent() && this.setState('disconnected'))

    this.connection = connection
    this.setState('connecting')

    try {
      await connection.start()
      if (isCurrent()) this.setState('connected')
      // Superseded while negotiating: a newer attempt is now the live one, and this socket was
      // already stopped by the stop() that superseded it. Say nothing.
      else await connection.stop().catch(() => undefined)
    } catch {
      if (isCurrent()) this.setState('disconnected')
      // Not rethrown. A dashboard that cannot open a socket should still render its data from the
      // REST calls; real-time is an enhancement, not a prerequisite.
    }
  }

  async stop(): Promise<void> {
    this.cancelFlush()
    if (this.counterTimer) clearTimeout(this.counterTimer)
    this.counterTimer = null

    // Retires the current attempt, including one still negotiating: whatever it decides afterwards
    // is about a connection nobody is using any more.
    this.generation++

    const connection = this.connection
    this.connection = null
    this.setState('disconnected')
    await connection?.stop().catch(() => undefined)
  }

  /** Subscribes to one session's timeline. Only the detail view wants this firehose. */
  async watchSession(sessionId: string): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      await this.connection.invoke('WatchSession', sessionId).catch(() => undefined)
    }
  }

  async unwatchSession(sessionId: string): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      await this.connection.invoke('UnwatchSession', sessionId).catch(() => undefined)
    }
  }

  private scheduleFlush(): void {
    if (this.flushHandle !== null) return

    this.flushHandle = requestAnimationFrame(() => {
      this.flushHandle = null

      if (this.pendingSessions.size > 0) {
        const batch = [...this.pendingSessions.values()]
        this.pendingSessions.clear()
        this.sessionHandlers.forEach((handler) => handler(batch))
      }

      if (this.pendingBugEvents.length > 0) {
        const batch = this.pendingBugEvents
        this.pendingBugEvents = []
        this.bugHandlers.forEach((handler) => handler(batch))
      }
    })
  }

  private cancelFlush(): void {
    if (this.flushHandle !== null) cancelAnimationFrame(this.flushHandle)
    this.flushHandle = null
    this.pendingSessions.clear()
    this.pendingBugEvents = []
  }

  private setState(state: ConnectionState): void {
    this.state = state
    this.stateHandlers.forEach((handler) => handler(state))
  }

  get connectionState(): ConnectionState {
    return this.state
  }

  onSessions(handler: Handler<LiveSession[]>): () => void {
    this.sessionHandlers.add(handler)
    return () => this.sessionHandlers.delete(handler)
  }

  onTimeline(handler: Handler<{ sessionId: string; entry: TimelineEntry }>): () => void {
    this.timelineHandlers.add(handler)
    return () => this.timelineHandlers.delete(handler)
  }

  onCounters(handler: Handler<void>): () => void {
    this.counterHandlers.add(handler)
    return () => this.counterHandlers.delete(handler)
  }

  onBugEvents(handler: Handler<BugHuntEvent[]>): () => void {
    this.bugHandlers.add(handler)
    return () => this.bugHandlers.delete(handler)
  }

  onInboxChanged(handler: Handler<void>): () => void {
    this.inboxHandlers.add(handler)
    return () => this.inboxHandlers.delete(handler)
  }

  onPromptQueued(handler: Handler<string>): () => void {
    this.queuedHandlers.add(handler)
    return () => this.queuedHandlers.delete(handler)
  }

  onRoomMessage(handler: Handler<RoomChatMessage>): () => void {
    this.roomHandlers.add(handler)
    return () => this.roomHandlers.delete(handler)
  }

  onStateChange(handler: Handler<ConnectionState>): () => void {
    this.stateHandlers.add(handler)
    return () => this.stateHandlers.delete(handler)
  }
}

export const realtime = new RealtimeClient()
